import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave } from "@fortawesome/free-solid-svg-icons";
import { Container, Form, Button, Row, Spinner, Alert } from 'react-bootstrap'; // Alert इंपोर्ट किया
import { useAddNewEventMutation } from "./eventsApiSlice";

const NewEventForm = () => {
    const [addNewEvent, {
        isLoading,
        isSuccess,
        isError,
        error
    }] = useAddNewEventMutation();

    const navigate = useNavigate();

    const [eventData, setEventData] = useState({
        name: '',
        date: '',
        time: '',
        duration: '',
        organizer: '',
        place: '',
        category: '',
        eventType: 'Offline', // Default to Offline
        zoomLink: '', // New field for Zoom link
        googleMapUrl: '', // New field for Google Map URL
        errors: {}
    });

    const [selectedFile, setSelectedFile] = useState(null); // State to hold the actual file object
    const [formError, setFormError] = useState(''); // To display API errors or form-wide errors

    useEffect(() => {
        if (isSuccess) {
            setEventData({ // Reset form data on success
                name: '',
                date: '',
                time: '',
                duration: '',
                organizer: '',
                place: '',
                category: '',
                eventType: 'Offline',
                zoomLink: '',
                googleMapUrl: '', // Reset googleMapUrl
                errors: {}
            });
            setSelectedFile(null); // Reset file input
            setFormError(''); // Clear any form error
            navigate('/dashboard/events');
        }
    }, [isSuccess, navigate]);

    useEffect(() => {
        if (isError) {
            const errorMessage = error?.data?.message || error?.message || "एक अनपेक्षित त्रुटि हुई।";
            console.error("API Error:", error);
            setFormError(errorMessage); // Display API error message to the user
        }
    }, [isError, error]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEventData(prevData => ({
            ...prevData,
            [name]: value,
            errors: { ...prevData.errors, [name]: '' },
        }));
    };

    const handleFileChange = (e) => {
        setSelectedFile(e.target.files[0]);
        setEventData(prevData => ({
            ...prevData,
            errors: { ...prevData.errors, banner: '' }, // Clear any banner error
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError(''); // Clear previous form errors

        const { name, date, organizer, place, time, category, eventType, zoomLink,googleMapUrl } = eventData;
        const errors = {};

        // Basic Validation
        if (!name.trim()) errors.name = 'इवेंट का नाम आवश्यक है।';
        if (!organizer.trim()) errors.organizer = 'आयोजक का नाम आवश्यक है।';
        if (!date.trim()) errors.date = 'इवेंट की तारीख आवश्यक है।';
        if (!time.trim()) errors.time = 'इवेंट का समय आवश्यक है।';
        if (!category.trim()) errors.category = 'इवेंट की श्रेणी आवश्यक है।';
        if (!place.trim()) errors.place = 'इवेंट का स्थान आवश्यक है।';
        if (!googleMapUrl.trim()) {
                errors.googleMapUrl = 'मानचित्र लिंक आवश्यक है।';
            }
        // Conditional Validation based on category and eventType (matches backend)
        if (category === 'Yearly') {
            if (!selectedFile) {
                errors.banner = 'वार्षिक इवेंट के लिए एक बैनर छवि आवश्यक है।';
            }
            if ((eventType === 'Online' || eventType === 'Hybrid') && !zoomLink.trim()) {
                errors.zoomLink = 'ऑनलाइन या हाइब्रिड वार्षिक इवेंट के लिए ज़ूम लिंक आवश्यक है।';
            }
        } else {
            // For other categories, if eventType is Online or Hybrid, zoomLink is required
            if ((eventType === 'Online' || eventType === 'Hybrid') && !zoomLink.trim()) {
                errors.zoomLink = 'ऑनलाइन या हाइब्रिड इवेंट के लिए ज़ूम लिंक आवश्यक है।';
            }
        }


        if (Object.keys(errors).length > 0) {
            setEventData({ ...eventData, errors });
            return;
        }

        const formData = new FormData();
        formData.append('name', name);
        formData.append('date', date);
        formData.append('time', time);
        formData.append('place', place);
        formData.append('organizer', organizer);
        formData.append('category', category);
        formData.append('eventType', eventType); // Append eventType
        if (eventData.duration) {
            formData.append('duration', eventData.duration);
        }
        if (zoomLink.trim()) { // Only append if zoomLink has a value
            formData.append('zoomLink', zoomLink.trim());
        }
        if (googleMapUrl.trim()) { // Only append if zoomLink has a value
            formData.append('googleMapUrl', googleMapUrl.trim());
        }
        if (selectedFile) {
            formData.append('banner', selectedFile);
        }

        if (!isLoading) {
            await addNewEvent(formData);
        }
    };

    const { errors } = eventData;
    const errClass = formError ? "errmsg" : "offscreen";

    const content = (
        <>
            <h2 className="pageHeading">नया इवेंट बनाएँ</h2>
            {formError && <Alert variant="danger" className={errClass}>{formError}</Alert>}

            <Container>
                <Row className="justify-content-md-center">
                    <Form onSubmit={handleSubmit}>
                        <div className='formGroup'>
                            <label htmlFor="eventName">इवेंट का नाम<sup>*</sup>:</label>
                            <input
                                type='text'
                                id="eventName"
                                name='name'
                                placeholder='इवेंट का नाम दर्ज करें'
                                value={eventData.name}
                                onChange={handleInputChange}
                            />
                            {errors.name && <span className="error">{errors.name}</span>}
                        </div>

                        <div className='formGroup'>
                            <label htmlFor="eventOrganizer">आयोजक<sup>*</sup>:</label>
                            <input
                                type='text'
                                id="eventOrganizer"
                                name='organizer'
                                value={eventData.organizer}
                                onChange={handleInputChange}
                                placeholder='आयोजक का नाम दर्ज करें'
                            />
                            {errors.organizer && <span className="error">{errors.organizer}</span>}
                        </div>

                        <div className='flex gap-4'>
                            <div className='formGroup'>
                                <label htmlFor="eventDate">इवेंट की तारीख<sup>*</sup>:</label>
                                <input
                                    type='date'
                                    id="eventDate"
                                    name='date'
                                    value={eventData.date}
                                    onChange={handleInputChange}
                                />
                                {errors.date && <span className="error">{errors.date}</span>}
                            </div>
                            <div className='formGroup'>
                                <label htmlFor="eventTime">इवेंट का समय<sup>*</sup>:</label>
                                <input
                                    type='time'
                                    id="eventTime"
                                    name='time'
                                    value={eventData.time}
                                    onChange={handleInputChange}
                                />
                                {errors.time && <span className="error">{errors.time}</span>}
                            </div>
                        </div>

                        <Form.Group controlId="formEventType" className="mb-3">
                            <Form.Label>इवेंट का प्रकार<sup>*</sup>:</Form.Label>
                            <Form.Select
                                aria-label="Event Type"
                                name="eventType"
                                value={eventData.eventType}
                                onChange={handleInputChange}
                            >
                                <option value="Offline">ऑफलाइन (केवल भौतिक स्थान)</option>
                                <option value="Online">ऑनलाइन (केवल Zoom/लाइव स्ट्रीम)</option>
                                <option value="Hybrid">हाइब्रिड (भौतिक और ऑनलाइन दोनों)</option>
                            </Form.Select>
                            {errors.eventType && <span className="error">{errors.eventType}</span>}
                        </Form.Group>

                        {(eventData.eventType === 'Online' || eventData.eventType === 'Hybrid') && (
                            <div className='formGroup'>
                                <label htmlFor="zoomLink">ज़ूम/ऑनलाइन लिंक{eventData.category === 'Yearly' || eventData.eventType !== 'Offline' ? <sup>*</sup> : ''}:</label>
                                <input
                                    type='url' // Changed to URL type for better mobile keyboard
                                    id="zoomLink"
                                    name='zoomLink'
                                    placeholder='Zoom मीटिंग या लाइव स्ट्रीम का URL दर्ज करें'
                                    value={eventData.zoomLink}
                                    onChange={handleInputChange}
                                />
                                {errors.zoomLink && <span className="error">{errors.zoomLink}</span>}
                            </div>
                        )}

                        <div className='formGroup'>
                                <label htmlFor="googleMapUrl">मानचित्र लिंक<sup>*</sup> :</label>
                                <input
                                    type='url' // Changed to URL type for better mobile keyboard
                                    id="googleMapUrl"
                                    name='googleMapUrl'
                                    placeholder='मानचित्र URL दर्ज करें'
                                    value={eventData.googleMapUrl}
                                    onChange={handleInputChange}
                                />
                                {errors.googleMapUrl && <span className="error">{errors.googleMapUrl}</span>}
                            </div>

                        <Form.Group controlId="formFile" className="mb-3">
                            <Form.Label>इवेंट बैनर {eventData.category === 'Yearly' ? <sup>*</sup> : '(वैकल्पिक)'}:</Form.Label>
                            <Form.Control
                                type="file"
                                name='banner'
                                accept="image/*"
                                onChange={handleFileChange}
                            />
                            {errors.banner && <span className="error">{errors.banner}</span>}
                        </Form.Group>

                        <div className='formGroup'>
                            <label htmlFor="eventCategory">इवेंट श्रेणी<sup>*</sup>:</label>
                            <Form.Select
                                aria-label="Event Category"
                                name="category"
                                value={eventData.category}
                                onChange={handleInputChange}
                            >
                                <option value=''>इवेंट श्रेणी चुनें</option>
                                <option value="Daily">दैनिक इवेंट</option>
                                <option value="Weekly">साप्ताहिक इवेंट</option>
                                <option value="Monthly">मासिक इवेंट</option>
                                <option value="Yearly">वार्षिक इवेंट</option>
                            </Form.Select>
                            {errors.category && <span className="error">{errors.category}</span>}
                        </div>

                        <div className='formGroup'>
                            <label htmlFor="eventPlace">इवेंट का स्थान<sup>*</sup>:</label>
                            <textarea
                                id="eventPlace"
                                name='place'
                                placeholder='इवेंट का पूरा पता दर्ज करें'
                                value={eventData.place}
                                onChange={handleInputChange}
                                className='long-content'
                            ></textarea>
                            {errors.place && <span className="error">{errors.place}</span>}
                        </div>

                        <Button variant="primary" type="submit" disabled={isLoading}>
                            <FontAwesomeIcon icon={faSave} className="me-2" />
                            इवेंट बनाएँ {isLoading ? (
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="ms-2"
                                />
                            ) : null}
                        </Button>
                    </Form>
                </Row>
            </Container>
        </>
    );

    return content;
};

export default NewEventForm;